import {reactive} from 'vue'
import type { MapDrawLayerManager } from './MapDrawLayerManager.ts'

export type Location = google.maps.LatLng | {
  lat: number | (() => number),
  lng: number | (() => number)
}

/**
 * フィーチャーの視覚的スタイル定義型
 * Google Maps Data APIにおけるポリゴンやマーカーの基本スタイル属性
 *
 * @property {string} [fillColor] - ポリゴンの塗りつぶし色（CSSカラー形式）
 * @property {number} [fillOpacity] - ポリゴンの塗りつぶし透明度（0～1の値）
 * @property {string} [strokeColor] - ポリゴンの境界線色（CSSカラー形式）
 * @property {number} [strokeWeight] - ポリゴンの境界線の太さ（ピクセル単位）
 * @property {number} [strokeOpacity] - ポリゴンの境界線の透明度（0～1の値）
 * @property {boolean} [visible] - ポリゴンの表示/非表示状態
 */
export type FeatureStyles = {
  fillColor?: string
  fillOpacity?: number
  strokeColor?: string
  strokeWeight?: number
  strokeOpacity?: number
  visible?: boolean
  [key: string]: string | number | boolean | undefined
}

/**
 * エリア機能の設定型定義
 * 基本的な視覚スタイル（FeatureStyles）を継承し、エリア固有の設定を追加
 *
 * @extends FeatureStyles
 *
 * @property {number} radius - 円形ポリゴンの半径（メートル）
 * @property {boolean} editable - ポリゴンの編集可否
 * @property {number} minRadius - 設定可能な最小半径（メートル）
 * @property {number} maxRadius - 設定可能な最大半径（メートル）
 * @property {number} vertex - ポリゴンを構成する頂点の数（多いほど円に近くなる）
 * @property {boolean} showArea - ポリゴンの表示/非表示状態
 * @property {boolean} showCenterMarker - 中心マーカーの表示/非表示
 * @property {string | null} markerImageUrl - マーカー画像のURL（nullの場合はデフォルトマーカー）
 * @property {{ width: number; height: number }} [markerSize] - マーカー画像のサイズ
 * @property {{ x: number; y: number }} [markerAnchor] - マーカーのアンカーポイント
 * @property {number} [markerZIndex] - マーカーのz-index値
 */
export type AreaOptions = FeatureStyles & {
  // 必須のスタイルプロパティ（FeatureStylesをオーバーライド）
  fillColor: string
  fillOpacity: number
  strokeColor: string
  strokeWeight: number
  strokeOpacity: number

  // エリア固有の設定
  radius: number
  editable: boolean
  minRadius: number
  maxRadius: number
  vertex: number
  showArea: boolean
  showCenterMarker: boolean
  markerImageUrl: string | undefined

  // オプショナルなマーカー設定
  markerSize?: { width: number; height: number } | undefined
  markerAnchor?: { x: number; y: number } | undefined
  markerZIndex?: number | undefined
}

/**
 * レイヤーに描画可能なコンテンツの共通インターフェース
 * 描画コンテンツ共通メソッドを提供する
 */
export interface DrawLayerContent<OptionsType = Record<string, unknown>> {
  /**
   * コンテンツを有効化する
   * enableはorganizations以下でのみ呼ぶこと、commonsの中で呼ぶと他組織でも検索時に表示されるようになるため注意
   * @returns {boolean} 有効化に成功したらtrue
   */
  enable(): boolean;

  /**
   * コンテンツを無効化し、描画されたオブジェクトを削除する
   * @returns {boolean} 無効化に成功したらfalse
   */
  disable(): boolean

  /**
   * 指定された位置にコンテンツを描画する
   * @param {Location} location - 描画する位置
   * @param options 描画オプション
   * @returns {string | null} 作成されたオブジェクトのID
   */
  draw(location: Location, options?: Partial<OptionsType>, featureId?: string): string | null

  /**
   * すべての描画されたコンテンツを削除する
   */
  remove(): void

  /**
   * コンテンツの表示オプションを更新する
   * @param {any} options - 更新するオプション
   * @returns {any} 更新後のオプション
   */
  updateOptions(options: Partial<OptionsType>): OptionsType
}

export interface FeatureData {
  feature: google.maps.Data.Feature;
  geometry: {
    center: google.maps.LatLng,
    radius: number
  }
  styles: FeatureStyles;
  marker?: google.maps.marker.AdvancedMarkerElement;
}

/**
 * エリア（ポリゴン）コンテンツ管理クラス
 *
 * - エリア（ポリゴン）の描画と管理
 * - ポリゴンの描画ロジックの実装
 * - 中心マーカーの作成と管理
 * - ポリゴン固有のスタイル管理
 * - 個別ポリゴンのCRUD操作
 * - 複数ポリゴンの管理
 * - ポリゴンの状態管理
 */
export class AreaContent implements DrawLayerContent {
  private layer: google.maps.Data | null = null
  private map: google.maps.Map | null = null
  private mapManager: MapDrawLayerManager | null = null

  public state = reactive({
    enabled: undefined,
    multiplePolygons: undefined,
    centerPoint: null as google.maps.LatLng | null,
    activeFeatureId: null as string | null,
    // フィーチャー情報を拡張
    features: new Map<string, FeatureData>(),
    // グローバル設定（デフォルト値）
    options: {
      radius: 1000,
      fillColor: '#4285F4',
      fillOpacity: 0.15,
      strokeColor: '#4285F4',
      strokeWeight: 2,
      strokeOpacity: 0.5,
      editable: true,
      minRadius: 100,
      maxRadius: 5000,
      vertex: 36,
      showArea: true,
      showCenterMarker: true,
      markerImageUrl: undefined as string | undefined,
      markerSize: {width: 32, height: 32},
      markerAnchor: {x: -16, y: -16},
      markerZIndex: undefined as number | undefined
    }
  })

  constructor(map: google.maps.Map | null = null, layer: google.maps.Data | null = null) {
    this.map = map
    this.layer = layer
  }

  /**
   * 描画コンテキストを設定する
   *
   * @param {google.maps.Map} map - 描画対象のGoogleマップインスタンス
   * @param {google.maps.Data} layer - 描画に使用するデータレイヤー
   * @param {any} manager - MapDrawLayerManagerインスタンス
   */
  configureContext(map: google.maps.Map, layer: google.maps.Data, manager?: MapDrawLayerManager): void {
    this.map = map
    this.layer = layer
    if (manager) {
      this.mapManager = manager
    }
  }

  /**
   * エリア描画機能を有効化する
   * enableはorganizations以下でのみ呼ぶこと。commonsの中で呼ぶと他組織でも検索時に表示されるようになるため注意
   *
   * @returns {boolean} 常にtrue
   */
  public enable(): boolean {
    this.state.enabled = true
    return true
  }

  /**
   * エリア描画機能を無効化し、全ポリゴンを削除する
   *
   * @returns {boolean} 常にfalse
   */
  disable(): boolean {
    this.state.enabled = false
    this.remove()
    return false
  }

  /**
   * マルチプルポリゴンモードを有効化する
   * @param enabled
   */
  public enableMultiplePolygonsMode(): void {
    this.state.multiplePolygons = true
  }

  /**
   * マルチプルポリゴンモードを無効化する
   */
  public disableMultiplePolygonsMode(): void {
    this.state.multiplePolygons = false
  }

  /**
   * エリアと中心マーカーを描画する
   *
   * @param {Location} location - 描画位置（中心点の座標）
   * @param {Partial<AreaOptions>} options - 描画オプション（半径や色など）
   * @param {string} [featureId] - 更新する既存フィーチャーID（指定時は更新、省略時は新規作成）
   * @returns {string | null} 作成/更新されたフィーチャーID、失敗時はnull
   */
  public draw(location: Location, options: Partial<typeof this.state.options> = {}, featureId?: string): string | null {
    if (!this.state.enabled || !this.map || !this.layer) {
      return null
    }

    // オプションをマージ
    const mergedOptions = {...this.state.options, ...options}
    this.state.options = mergedOptions

    // 中心点の取得
    const centerPoint = new google.maps.LatLng(
      typeof location.lat === 'function' ? location.lat() : location.lat,
      typeof location.lng === 'function' ? location.lng() : location.lng
    )
    this.state.centerPoint = centerPoint

    const multiplePolygons = this.state.multiplePolygons || false
    const currentFeatureId = this.state.activeFeatureId

    // 更新か新規作成かを判断
    const isUpdate = featureId !== undefined ||
      (currentFeatureId !== null && options.radius !== undefined)
    const targetFeatureId = featureId || (isUpdate ? currentFeatureId : null)

    // console.debug("複数ポリゴンモード:", multiplePolygons, "更新モード:", isUpdate)

    // 単一ポリゴンモード時は、更新対象以外のポリゴンを削除
    if (!multiplePolygons) {
      // console.debug("単一ポリゴンモード: アクティブ以外のポリゴンを削除")
      this.removeAllExcept(targetFeatureId)
    }

    let resultId: string
    // 1. 明示的にfeatureIdが指定されている場合は必ず更新
    if (featureId) {
      // console.debug("指定されたIDのフィーチャーを更新:", featureId)
      resultId = this._updateFeature(featureId, centerPoint, mergedOptions.radius)
    }
    // 2. 複数ポリゴンモードでは常に新規作成（明示的なIDがない場合）
    else if (multiplePolygons) {
      // console.debug("複数ポリゴンモード: 新規フィーチャー作成")
      resultId = this._createFeature(centerPoint, mergedOptions.radius)
    }
    // 3. 単一ポリゴンモードで現在のフィーチャーがある場合
    else if (currentFeatureId) {
      // console.debug("単一ポリゴンモード: 現在のフィーチャーを更新:", currentFeatureId)
      resultId = this._updateFeature(currentFeatureId, centerPoint, mergedOptions.radius)
    }
    // 4. それ以外は新規作成
    else {
      // console.debug("フィーチャーがないため新規作成")
      resultId = this._createFeature(centerPoint, mergedOptions.radius)
    }

    // console.debug("フィーチャー処理完了:", resultId, "現在の総フィーチャー数:", this.state.features.size)
    // console.debug("登録済みフィーチャーID:", [...this.state.features.keys()])

    // アクティブフィーチャーの設定
    this.state.activeFeatureId = resultId

    return resultId
  }

  /**
   * 指定されたID以外のすべてのフィーチャーを削除する
   *
   * @param {string | null} idToPreserve - 保持するフィーチャーID
   * @returns {boolean} 削除処理の成功・失敗
   */
  private removeAllExcept(idToPreserve: string | null): boolean {
    if (!this.layer || !this.map) {
      return false
    }

    try {
      // 削除対象のID一覧を作成
      const featureIdsToRemove: string[] = []
      this.state.features.forEach((_, id) => {
        if (id !== idToPreserve) {
          featureIdsToRemove.push(id)
        }
      })

      // 対象フィーチャーを削除
      for (const id of featureIdsToRemove) {
        const featureData = this.state.features.get(id)
        if (featureData) {
          if (featureData.marker) {
            featureData.marker.map = null
          }
          this.layer.remove(featureData.feature)
          this.state.features.delete(id)
        }
      }
      return true
    } catch (e) {
      console.error("削除処理中にエラー:", e)
      return false
    }
  }

  /**
   * 描画したすべてのフィーチャーを削除する
   */
  remove(): void {
    if (!this.layer || !this.map) {
      return
    }
    try {
      // 1. マーカーを確実に削除
      for (const featureData of this.state.features.values()) {
        if (featureData.marker) {
          featureData.marker.map = null
        }
      }

      // 2. データレイヤーを完全に再作成
      this.layer.setMap(null)

      // 3. 新しいレイヤーを作成
      const newLayer = new google.maps.Data()
      newLayer.setMap(this.map)

      // 4. レイヤー参照を更新
      this.layer = newLayer

      // 5. 状態をクリア
      this.state.features.clear()
      this.state.activeFeatureId = null

      // console.debug("全ポリゴン削除完了 - レイヤー参照更新済み")
    } catch (e) {
      // console.error("削除処理中にエラー:", e)
    }

    // console.debug("=== 削除処理完了 ===")
  }

  /**
   * エリア描画のオプションを更新する
   *
   * @param {Partial<AreaOptions>} options - 更新するオプション（部分指定可）
   * @returns {AreaOptions} 更新後の完全なオプション設定
   */
  public updateOptions(options: Partial<AreaOptions> = {}): typeof this.state.options {
    this.state.options = {...this.state.options, ...options}

    // 表示関連のオプションが変更された場合
    if (options.showCenterMarker !== undefined ||
      options.markerImageUrl !== undefined ||
      options.showArea !== undefined) {
      this.refreshMapVisibility()
    } else {
      // 表示以外のスタイル変更のみの場合
      this.applyLayerStyles()
    }

    return this.state.options
  }

  /**
   * 既存のエリアの半径を更新する専用メソッド
   *
   * @param {number} radius - 新しい半径（メートル）
   * @param {string} [featureId] - 更新対象のフィーチャーID（省略時は現在のアクティブフィーチャー）
   * @returns {boolean} 更新成功時はtrue、失敗時はfalse
   */
  public updateRadius(radius: number, featureId?: string): boolean {
    const targetId = featureId || this.state.activeFeatureId
    if (!targetId) {
      return false
    }

    const featureData = this.state.features.get(targetId)
    if (!featureData || !this.layer) {
      return false
    }

    // 既存の中心座標を使用して半径のみ更新
    const center = featureData.geometry.center

    // ポリゴン頂点の再計算
    const points = this.state.options.vertex
    const polygonPoints = this._calculatePolygonPoints(center, radius, points)

    // ジオメトリのみを更新
    const newPolygon = new google.maps.Data.Polygon([polygonPoints])
    featureData.feature.setGeometry(newPolygon)

    // プロパティとフィーチャーデータを更新
    featureData.feature.setProperty('radius', radius)
    featureData.geometry.radius = radius

    // スタイル適用
    this.applyLayerStyles()

    // グローバルオプション値も更新（一貫性のため）
    this.state.options.radius = radius

    return true
  }

  /**
   * 新しいフィーチャー（ポリゴンとマーカー）を作成する
   *
   * @param {google.maps.LatLng} center - ポリゴンの中心点
   * @param {number} [radius] - ポリゴンの半径（メートル単位）
   * @returns {string} 作成されたフィーチャーのID
   * @private
   */
  private _createFeature(center: google.maps.LatLng, radius?: number): string {
    if (!this.layer || !this.map) {
      return ''
    }

    // ポリゴン頂点の計算
    const points = this.state.options.vertex
    const radiusInMeters = radius || this.state.options.radius
    const polygonPoints = this._calculatePolygonPoints(center, radiusInMeters, points)

    // フィーチャーID生成
    const featureId = 'polygon_' + Date.now()

    // ポリゴンフィーチャーの作成
    const polygonFeature = this.layer.add({
      id: featureId,
      geometry: new google.maps.Data.Polygon([polygonPoints]),
      properties: {
        type: 'searchArea',
        radius: radiusInMeters,
        centerLat: center.lat(),
        centerLng: center.lng()
      }
    })

    // マーカーの作成
    const marker = this.state.options.showCenterMarker ?
      this._createMarker(center) : undefined

    // フィーチャーをMap管理に追加
    this.state.features.set(featureId, {
      feature: polygonFeature,
      geometry: {
        center: center,
        radius: radiusInMeters
      },
      styles: {
        visible: this.state.options.showArea
      },
      marker
    })

    // スタイル適用
    this.applyLayerStyles()

    return featureId
  }

  /**
   * フィーチャーを更新する
   * ポリゴンの座標や半径を変更する
   *
   * @param {string} featureId - 更新するフィーチャーのID
   * @param {google.maps.LatLng} center - 新しい中心座標
   * @param {number} [radius] - 新しい半径（メートル、省略時は現在のオプション設定値を使用）
   * @returns {string} フィーチャーID、失敗時は空文字
   * @private
   */
  private _updateFeature(featureId: string, center: google.maps.LatLng, radius?: number): string {
    const featureData = this.state.features.get(featureId)
    if (!featureData || !this.layer) {
      return ''
    }
    console.debug("_updateFeature", featureId)

    // 半径の決定（指定された値または現在のオプション値を使用）
    const radiusInMeters = radius || this.state.options.radius

    // ポリゴン頂点の計算
    const points = this.state.options.vertex
    const polygonPoints = this._calculatePolygonPoints(center, radiusInMeters, points)

    // ジオメトリのみを更新（フィーチャー自体は削除せず）
    const newPolygon = new google.maps.Data.Polygon([polygonPoints])
    featureData.feature.setGeometry(newPolygon)

    // プロパティの更新
    featureData.feature.setProperty('radius', radiusInMeters)
    featureData.feature.setProperty('centerLat', center.lat())
    featureData.feature.setProperty('centerLng', center.lng())

    // マーカーの更新（中心点が変わった場合のみ）
    if (featureData.marker && !featureData.geometry.center.equals(center)) {
      // console.debug("_updateFeature // 中心が変わった場合のみマーカーを更新")
      // 中心が変わった場合のみマーカーを更新
      featureData.marker.map = null
      if (this.state.options.showCenterMarker) {
        // console.debug("_updateFeature // _createMarker")
        featureData.marker = this._createMarker(center)
      } else {
        featureData.marker = undefined
      }
    } else if (!featureData.marker && this.state.options.showCenterMarker) {
      // console.debug("_updateFeature // マーカーがなく表示設定の場合は新規作成")
      // マーカーがなく表示設定の場合は新規作成
      featureData.marker = this._createMarker(center)
    } else if (featureData.marker && !this.state.options.showCenterMarker) {
      // console.debug("_updateFeature // // 非表示設定の場合はマーカーを削除")
      featureData.marker.map = null
      featureData.marker = undefined
    }

    // フィーチャー情報を更新
    featureData.geometry.center = center
    featureData.geometry.radius = radiusInMeters

    // スタイル適用
    this.applyLayerStyles()

    return featureId
  }

  /**
   * ポリゴンの頂点を計算
   * @private
   */
  private _calculatePolygonPoints(center: google.maps.LatLng, radius: number, points: number): google.maps.LatLng[] {
    const polygonPoints = []

    for (let i = 0; i < points; i++) {
      const angle = i * (360 / points)
      const latLng = google.maps.geometry.spherical.computeOffset(
        center, radius, angle
      )
      polygonPoints.push(latLng)
    }

    // 閉じたポリゴンにするため最初の点を最後にも追加
    polygonPoints.push(polygonPoints[0])

    return polygonPoints
  }

  /**
   * 中心マーカーを作成
   * @private
   */
  private _createMarker(position: google.maps.LatLng): google.maps.marker.AdvancedMarkerElement | undefined {
    if (!this.map) {
      return undefined
    }

    // コンテナ作成（位置調整用）
    const markerContainer = document.createElement('div')
    markerContainer.style.position = 'relative'
    markerContainer.style.width = '0'
    markerContainer.style.height = '0'

    // マーカー要素（実際に表示される部分）
    const markerElement = document.createElement('div')
    markerElement.style.position = 'absolute'
    markerElement.style.width = `${this.state.options.markerSize?.width || 32}px`
    markerElement.style.height = `${this.state.options.markerSize?.height || 32}px`

    // アンカーポイントの設定
    const anchorX = this.state.options.markerAnchor?.x || -16
    const anchorY = this.state.options.markerAnchor?.y || -16
    markerElement.style.left = `${anchorX}px`
    markerElement.style.top = `${anchorY}px`

    if (this.state.options.markerImageUrl) {
      // 画像を使用する場合
      const img = document.createElement('img')
      img.src = this.state.options.markerImageUrl
      img.style.width = '100%'
      img.style.height = '100%'
      markerElement.appendChild(img)
    } else {
      // デフォルトマーカー（円形）
      markerElement.style.borderRadius = '50%'
      markerElement.style.backgroundColor = this.state.options.fillColor || '#4285F4'
      markerElement.style.border = '2px solid white'
    }

    markerContainer.appendChild(markerElement)

    // マーカー作成
    const marker = new google.maps.marker.AdvancedMarkerElement({
      position,
      map: this.map,
      content: markerContainer,
      zIndex: this.state.options.markerZIndex || 1
    })

    return marker
  }

  /**
   * レイヤースタイルを現在の設定に基づいて適用する
   * フィーチャーの表示/非表示やスタイル属性を制御する
   * 個別フィーチャーのスタイル設定があればそれを優先する
   *
   * @public
   */
  public applyLayerStyles(): void {
    if (!this.layer) {
      return
    }

    this.layer.setStyle(feature => {
      const featureId = feature.getId() as string
      const featureData = this.state.features.get(featureId)

      // 基本スタイル
      const baseStyle = {
        fillColor: this.state.options.fillColor,
        fillOpacity: this.state.options.fillOpacity,
        strokeColor: this.state.options.strokeColor,
        strokeWeight: this.state.options.strokeWeight,
        strokeOpacity: this.state.options.strokeOpacity,
        visible: this.state.options.showArea
      }

      // 個別フィーチャーのスタイルがあれば上書き
      if (featureData && featureData.styles) {
        return {...baseStyle, ...featureData.styles}
      }

      return baseStyle
    })
  }

  /**
   * すべてのマーカーとエリア表示を現在のオプション設定に基づいて更新する
   * マーカーの再作成と表示状態の切り替えを行う
   *
   * @private
   */
  private refreshMapVisibility(): void {
    // すべてのフィーチャーを処理
    for (const featureData of this.state.features.values()) {
      // マーカーの更新
      if (featureData.marker) {
        featureData.marker.map = null
      }

      if (this.state.options.showCenterMarker) {
        featureData.marker = this._createMarker(featureData.geometry.center)
      } else {
        featureData.marker = undefined
      }

      // エリア表示状態の更新
      featureData.styles.visible = this.state.options.showArea
    }

    // スタイル変更を適用
    this.applyLayerStyles()
  }
}